<!--
  Só as listas. O `eligibility_scope` é coluna do plano e é salvo pelo formulário
  junto com o resto — antes este componente também o editava e mandava um PUT do
  recurso inteiro ecoando o snapshot que tinha carregado, o que revertia em
  silêncio o que a outra aba havia gravado.

  Aulas e modalidades continuam aqui porque têm endpoint próprio: são tabelas de
  vínculo que só existem depois de o plano ter id, e cada anexo/remoção carrega
  trava de negócio própria no backend.
-->
<template>
  <div>
    <!-- Aulas específicas -->
    <template v-if="scope === 'LES'">
      <div class="w-full flex gap-2 justify-end mb-2">
        <b-button variant="primary" @click="showLessonPopup = true">
          <feather-icon icon="SearchIcon" class="mr-50"/>
          <span class="align-middle">Buscar aulas</span>
        </b-button>
      </div>

      <lesson-popup
        :visible="showLessonPopup"
        :plan-id="id"
        @selected="attachLessons"
        @close="showLessonPopup = $event"/>

      <vs-table :data="lessons" no-data-text="Nenhuma aula vinculada">
        <template slot="thead">
          <vs-th>Aula</vs-th>
          <vs-th>Modalidade</vs-th>
          <vs-th>Ação</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr :key="`les-${tr.id}`" v-for="tr in data">
            <vs-td>{{ tr.name }}</vs-td>
            <vs-td>{{ tr.modality_name || '—' }}</vs-td>
            <vs-td>
              <feather-icon
                icon="Trash2Icon"
                style="cursor: pointer;"
                class="h-5 w-5 stroke-current text-danger"
                @click="confirmDetachLesson(tr)"/>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </template>

    <!-- Modalidades -->
    <template v-else-if="scope === 'MOD'">
      <div class="w-full flex gap-2 justify-end mb-2">
        <b-button variant="primary" @click="showModalityPopup = true">
          <feather-icon icon="SearchIcon" class="mr-50"/>
          <span class="align-middle">Buscar modalidades</span>
        </b-button>
      </div>

      <modality-popup
        :visible="showModalityPopup"
        @selected="attachModalities"
        @close="showModalityPopup = $event"/>

      <vs-table :data="modalities" no-data-text="Nenhuma modalidade vinculada">
        <template slot="thead">
          <vs-th>Modalidade</vs-th>
          <vs-th>Ação</vs-th>
        </template>
        <template slot-scope="{data}">
          <vs-tr :key="`mod-${tr.id}`" v-for="tr in data">
            <vs-td>{{ tr.name }}</vs-td>
            <vs-td>
              <feather-icon
                icon="Trash2Icon"
                style="cursor: pointer;"
                class="h-5 w-5 stroke-current text-danger"
                @click="confirmDetachModality(tr)"/>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </template>

    <div v-else class="text-muted p-2">
      Este plano cobre <strong>todas as aulas</strong> do estabelecimento. Aulas novas
      passam a valer automaticamente.
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import SubscriptionPlanService from '@/services/api/SubscriptionPlanService'
import LessonPopup from '../lesson/LessonPopup.vue'
import ModalityPopup from '../modality/ModalityPopup.vue'

export default {
  name: 'SubscriptionPlanEligibility',
  components: {
    BButton, LessonPopup, ModalityPopup,
  },
  props: {
    id: { type: [String, Number], required: true },
    /** Vem do formulário, que é quem grava a coluna. Aqui só decide o que listar. */
    scope: { type: String, default: 'ALL' },
  },
  data() {
    return {
      lessons: [],
      modalities: [],
      showLessonPopup: false,
      showModalityPopup: false,
    }
  },
  mounted() {
    this.loadLists()
  },
  methods: {
    loadLists() {
      const service = SubscriptionPlanService.build(this.$vs)
      service.getLessons(this.id).then(data => { this.lessons = Array.isArray(data) ? data : [] })
      service.getModalities(this.id).then(data => { this.modalities = Array.isArray(data) ? data : [] })
    },
    attachLessons(ids) {
      if (!Array.isArray(ids) || ids.length === 0) return
      this.$vs.loading()
      SubscriptionPlanService.build(this.$vs).addLessons(this.id, ids).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.loadLists()
          this.$emit('changed')
        },
        error => this.notifyErrors(this.$vs, error),
      ).finally(() => this.$vs.loading.close())
    },
    attachModalities(ids) {
      if (!Array.isArray(ids) || ids.length === 0) return
      this.$vs.loading()
      SubscriptionPlanService.build(this.$vs).addModalities(this.id, ids).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.loadLists()
          this.$emit('changed')
        },
        error => this.notifyErrors(this.$vs, error),
      ).finally(() => this.$vs.loading.close())
    },
    confirmDetachLesson(lesson) {
      this.$vs.dialog({
        type: 'confirm', color: 'success', title: 'Confirmação',
        acceptText: 'Sim', cancelText: 'Não',
        text: `Remover "${lesson.name}" das aulas incluídas neste plano?`,
        accept: () => {
          SubscriptionPlanService.build(this.$vs).deleteLesson(this.id, lesson.id).then(() => {
            this.lessons = this.lessons.filter(item => item.id !== lesson.id)
            this.notifySuccess(this.$vs, this.$t('messages.success'))
            this.$emit('changed')
          })
        },
      })
    },
    confirmDetachModality(modality) {
      this.$vs.dialog({
        type: 'confirm', color: 'success', title: 'Confirmação',
        acceptText: 'Sim', cancelText: 'Não',
        text: `Remover "${modality.name}" das modalidades deste plano?`,
        accept: () => {
          SubscriptionPlanService.build(this.$vs).deleteModality(this.id, modality.id).then(() => {
            this.modalities = this.modalities.filter(item => item.id !== modality.id)
            this.notifySuccess(this.$vs, this.$t('messages.success'))
            this.$emit('changed')
          })
        },
      })
    },
  },
}
</script>
